import { fg } from '@atlaskit/platform-feature-flags/fg';
import { buildAtlAttributionHeaders } from '@atlassian/search-client/atl-attribution';
import { loomTagsSearchQuery } from '@atlassian/search-client/loom/tag-search';

import type { CloudConfig } from '../../../../../common/constants/filters/cloud-config/types';
import type {
	SelectFilterMonad,
	SelectFilterState,
} from '../../../../../common/constants/filters/select-filter/types';
import { ProductKeys } from '../../../../../common/constants/product-keys';
import { createMultiSelectFilter } from '../../../../filters/multi-select-filter';

const DEFAULT_LOOM_TAG_INITIAL_LIMIT = 15;
const DEFAULT_LOOM_TAG_LOOKUP_LIMIT = 15;

const getFilterProducts = (): ProductKeys[] => (fg('loom-rovo-search') ? [ProductKeys.Loom] : []);

const fetchLoomTagOptions = async (query: string, limit: number, config: CloudConfig) => {
	const cloudId = config.cloudId;
	if (!cloudId) {
		return [];
	}

	const headers = buildAtlAttributionHeaders({
		cloudId,
		activationId: config.activationId,
		product: config.primaryProduct,
	});
	const response = await loomTagsSearchQuery({
		variables: {
			siteId: cloudId,
			query,
			limit,
		},
		aggAbsoluteUrl: config.aggAbsoluteUrl,
		headers,
	});

	return (
		response.data?.loom_tagsSearch?.map(({ tag }) => ({
			trackingKey: tag,
			value: tag,
			queryParamValue: tag,
			label: tag,
			product: ProductKeys.Loom,
		})) ?? []
	);
};

const createTagFilter = (products = getFilterProducts()) =>
	createMultiSelectFilter({
		id: 'tag',
		products,
		alwaysVisible: false,
		universal: false,
		queryParams: {
			key: 'tags',
			// Loom tag names are persisted directly, so hydration does not need another network request.
			fetcher: async (queryParams) =>
				queryParams.map((tag) => ({
					trackingKey: tag,
					value: tag,
					queryParamValue: tag,
					label: tag,
					product: ProductKeys.Loom,
				})),
		},
		initialOptions: {
			fetcher: async (config) => fetchLoomTagOptions('', DEFAULT_LOOM_TAG_INITIAL_LIMIT, config),
		},
		lookup: {
			fetcher: async (query, config) =>
				fetchLoomTagOptions(query, DEFAULT_LOOM_TAG_LOOKUP_LIMIT, config),
		},
	});

// Keep module evaluation side-effect free. Runtime entry points below resolve the gated products.
const baseFilter = createTagFilter([]);

export const filter: Readonly<{
	id: 'tag';
	type: 'single-select' | 'multi-select';
	queryParams: {
		key: string;
		getState: (queryParams: Record<string, string[]>, cloudId: string) => SelectFilterState<'tag'>;
	};
	products: ProductKeys[];
	universal: boolean;
	customValueFields?: string[];
	withState: (state: unknown, config: CloudConfig) => SelectFilterMonad<'tag'>;
	getInitialState: (config: CloudConfig) => SelectFilterMonad<'tag'>;
	isCompatible: (filterId?: string) => boolean;
	isSupportCustomValue: boolean;
	isSuggestedFilter?: (primaryProduct?: string) => boolean;
}> = {
	...baseFilter,
	queryParams: {
		...baseFilter.queryParams,
		getState: (queryParams, cloudId) => {
			const products = getFilterProducts();
			const state = createTagFilter(products).queryParams.getState(queryParams, cloudId);

			return {
				...state,
				value: products.length > 0 ? state.value : [],
				products,
			};
		},
	},
	withState: (state, config) =>
		createTagFilter().withState(
			{
				...(state as SelectFilterState<'tag'>),
				products: getFilterProducts(),
			},
			config,
		),
	getInitialState: (config) => createTagFilter().getInitialState(config),
	get products() {
		return getFilterProducts();
	},
};
